import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ReportControls } from "@/components/reports/ReportControls";
import { GrossProfitReport, NetProfitReport } from "@/components/reports/StatementReports";
import { ExpenseBreakdown } from "@/components/reports/ExpenseBreakdown";
import { RevenueVsCost } from "@/components/reports/RevenueVsCost";
import { CostPerUnit } from "@/components/reports/CostPerUnit";
import { PnlSummaryTable } from "@/components/reports/PnlSummaryTable";
import { MonthlyPnl } from "@/components/reports/MonthlyPnl";
import { apiError } from "@/lib/api";
import { useSource } from "@/context/SourceContext";
import { useAuth } from "@/context/AuthContext";
import { QUICK_RANGES, rangeError } from "@/lib/ranges";

export default function ReportsPage() {
  const { user } = useAuth() || {};
  const { readOnly } = useSource();
  if (user?.is_task_user && !readOnly && !user.sections.includes("reports")) {
    return <div className="space-y-6" data-testid="reports-page"><PageHeader brand eyebrow="Reports" title="Monthly Profit and Loss Statement" /><MonthlyPnl /></div>;
  }
  return <FullReports />;
}

function FullReports() {
  const [initFrom, initTo] = QUICK_RANGES[0].range();
  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);
  const [view, setView] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const error = rangeError(from, to, view);
  const src = useSource();

  useEffect(() => {
    if (error) return undefined;
    const ctl = new AbortController();
    setLoading(true);
    src.get("/pnl/summary", { params: { from, to, granularity: view }, signal: ctl.signal })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { if (!axios.isCancel(e)) { toast.error(apiError(e)); setLoading(false); } });
    return () => ctl.abort();
  }, [src, from, to, view, error]);

  const params = { from, to, granularity: view };
  const ready = data && !error;
  const props = { data, params, disabled: !ready };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader brand eyebrow="Tools" title="Reports" subtitle="Profit & loss analysis for any period · all amounts in Omani Rial" />
      <ReportControls from={from} to={to} view={view} error={error} loading={loading} onFrom={setFrom} onTo={setTo} onView={setView}
        onRange={([f, t]) => { setFrom(f); setTo(t); }} params={params} disabled={!ready} />
      {data?.totals?.gp_note && <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="reports-gp-note">{data.totals.gp_note}: {data.totals.gp_missing_months.join(", ")}</p>}
      {data?.note && <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="reports-excluded-note">{data.note}</p>}
      {data && (
        <div className={`space-y-6 transition-opacity ${loading || error ? "opacity-50" : ""}`} data-testid="report-sections">
          <div className="grid gap-6 xl:grid-cols-2">
            <GrossProfitReport {...props} />
            <NetProfitReport {...props} />
          </div>
          <ExpenseBreakdown {...props} />
          <RevenueVsCost {...props} />
          <CostPerUnit {...props} />
          <PnlSummaryTable {...props} />
        </div>
      )}
      <MonthlyPnl />
    </div>
  );
}
